import { apiClient, ApiClient } from './client';
import type { CharacterListResponse, CharacterDetailResponse, CreateCharacterPayload, UpdateCharacterPayload, ChatHistoryPayload, Visibility } from './types';

class Character {
  private readonly apiClient: ApiClient;
  public readonly id: string;
  public username: string;
  public display_name: string;
  public tagline: string;
  public icon: string;
  public description: string;
  public external_description?: string;
  public author: string;
  public greeting: string;
  public is_mature: boolean;
  public visibility: Visibility;
  constructor(username: string, apiClient: ApiClient, details: CharacterDetailResponse) {
    this.username = username;
    this.apiClient = apiClient;
    this.id = details.id;
    this.display_name = details.display_name;
    this.tagline = details.tagline;
    this.icon = details.icon;
    this.description = details.description;
    this.external_description = details.external_description;
    this.greeting = details.greeting;
    this.author = details.author;
    this.is_mature = details.is_mature;
    this.visibility = details.visibility;
  }

  async chat_history(): Promise<ChatHistoryPayload[]> {
    try {
      const response = await this.apiClient.get(`/chat-history/${this.id}/`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch chat history:', error);
      throw error;
    }
  }

  async delete(): Promise<void> {
    try {
      await this.apiClient.delete(`/characters/${this.username}/`);
    } catch (error) {
      console.error('Failed to delete character:', error);
      throw error;
    }
  }

  async update(data: UpdateCharacterPayload): Promise<Character> {
    try {
      console.log('Character update called with data:', data);
      console.log('Current character state before update:', {
        username: this.username,
        visibility: this.visibility
      });
      
      const formData = new FormData();
      if (data.display_name) formData.append('display_name', data.display_name);
      if (data.username) formData.append('username', data.username);
      if (data.tagline) formData.append('tagline', data.tagline);
      if (data.icon) formData.append('icon', data.icon);
      if (data.greeting) formData.append('greeting', data.greeting);
      if (data.description) formData.append('description', data.description);
      if (data.external_description) formData.append('external_description', data.external_description);
      if (data.visibility) {
        console.log('Setting visibility in formData:', data.visibility);
        formData.append('visibility', data.visibility);
      }

      console.log('Sending update request for character:', this.username, 'with data:', Object.fromEntries(formData.entries()));
      
      const response = await this.apiClient.patch(`/characters/${this.username}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Character update response:', response.data);
      
      // Return a new Character instance instead of modifying this one
      return new Character(response.data.username, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to update character:', error);
      throw error;
    }
  }

  async get(): Promise<Character> {
    try {
      const response = await this.apiClient.get(`/characters/${this.username}/`);
      return new Character(response.data.username, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch character details:', error);
      throw error;
    }
  }

  // Add a dedicated method for updating visibility
  async updateVisibility(newVisibility: Visibility): Promise<Character> {
    try {
      console.log(`Updating visibility specifically from ${this.visibility} to ${newVisibility}`);
      
      // Create a fresh FormData object
      const formData = new FormData();
      formData.append('visibility', newVisibility);
      
      // Make a direct API call to update only the visibility
      const response = await this.apiClient.patch(`/characters/${this.username}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      console.log('Visibility update response:', response.data);
      
      // Return a new Character instance instead of modifying this one
      return new Character(response.data.username, this.apiClient, response.data);
    } catch (error) {
      console.error('Failed to update visibility:', error);
      throw error;
    }
  }
}

export type CharacterFilter = 'trending' | 'featured' | 'top' | 'new';

export const characters = {
  async list(): Promise<CharacterListResponse[]> {
    try {
      const response = await apiClient.get('/characters/');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch characters:', error);
      throw error;
    }
  },

  async owned(is_agent_created: boolean | null = null): Promise<CharacterListResponse[]> {
    try {
      const params: { owned: boolean; is_agent_created?: boolean } = { 
        owned: true 
      };
      
      // Only include is_agent_created in params if it's explicitly true or false
      if (is_agent_created === true || is_agent_created === false) {
        params.is_agent_created = is_agent_created;
      }
      
      const response = await apiClient.get('/characters/', { params });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch owned characters:', error);
      throw error;
    }
  },

  async ownedBy(username: string, is_agent_created: boolean | null = null): Promise<CharacterListResponse[]> {
    try {
      const params: { owned_by: string; is_agent_created?: boolean } = {
        owned_by: username
      };
      
      // Only include is_agent_created in params if it's explicitly true or false
      if (is_agent_created === true || is_agent_created === false) {
        params.is_agent_created = is_agent_created;
      }
      
      const response = await apiClient.get('/characters/', { params });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch characters owned by ${username}:`, error);
      throw error;
    }
  },

  async create(data: CreateCharacterPayload): Promise<Character> {
    try {
      const formData = new FormData();
      formData.append('display_name', data.display_name);
      formData.append('tagline', data.tagline);
      formData.append('greeting', data.greeting);
      formData.append('description', data.description);
      if (data.external_description) {
        formData.append('external_description', data.external_description);
      }
      if (data.icon) {
        formData.append('icon', data.icon);
      }
      if (data.visibility) {
        formData.append('visibility', data.visibility);
      }

      const response = await apiClient.post('/characters/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return new Character(response.data.username, apiClient, response.data);
    } catch (error) {
      console.error('Failed to create character:', error);
      throw error;
    }
  },

  async get(username: string): Promise<Character> {
    try {
      const response = await apiClient.get(`/characters/${username}/`);
      return new Character(username, apiClient, response.data);
    } catch (error) {
      console.error('Failed to fetch character details:', error);
      throw error;
    }
  },

  async filter(type: CharacterFilter): Promise<CharacterListResponse[]> {
    try {
      const response = await apiClient.get('/characters/', {
        params: { filter: type }
      });
      return response.data;
    } catch (error) {
      console.error(`Failed to fetch ${type} characters:`, error);
      throw error;
    }
  },

  async search(query: string, exclude_mature: boolean = true): Promise<CharacterListResponse[]> {
    try {
      const response = await apiClient.get('/characters/', {
        params: { 
          search: query,
          exclude_mature
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to search characters:', error);
      throw error;
    }
  }
}; 

export type { Character as CharacterType };